import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import type { ChartHistoryFilterType } from "@roman/shared/constants/predictionChart";
import type { PredictionMarketSnapshot } from "@roman/shared/types/PredictionMarket";
import { extractChoices } from "@roman/shared/utils/predictionMarket/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { AnimatedDot, type AnimatedDotProps } from "./AnimatedDot";
import { FilterSelect } from "./FilterSelect";
import { PredictionChartMenu } from "./Menu";
import FilterIcon from "./icons/FilterIcon";
import { bucketChoiceProbabilities } from "./utils/bucketChoiceProbabilities";
import { createChartConfig } from "./utils/chartConfig";
import { getDefaultSelectedChoices } from "./utils/choiceSelection";
import { getCustomTicksFor1M, getCustomTicksFor1W, getCustomTicksForAll } from "./utils/customTicks";
import { getTickInterval, getXAxisFormatter } from "./utils/xAxisFormatters";
import { calculateYAxisDomain } from "./utils/yAxisFormatters";

interface PredictionChartProps {
  predictionMarketSnapshots: PredictionMarketSnapshot[];
  defaultShowChartsIds?: string[];
  setChoiceIds?: (ids: string[]) => void;
  endTime?: number | null;
  isLineVisible?: boolean;
}

export const PredictionChart = ({
  predictionMarketSnapshots,
  defaultShowChartsIds,
  setChoiceIds,
  endTime,
  isLineVisible = false,
}: PredictionChartProps) => {
  const [isActiveDotShown, setIsActiveDotShown] = useState(false);
  const [isPointerEventsEnabled, setIsPointerEventsEnabled] = useState(false);

  const chartRef = useRef<any>(null);

  const [displayMode, setDisplayMode] = useState<ChartHistoryFilterType>("all");

  const [isFilterSelectOpen, setIsFilterSelectOpen] = useState(false);

  const { sortedChoices: allChoices } = extractChoices(predictionMarketSnapshots);

  // Get top 4 choices based on latest probability
  const defaultSelectedChoices = useMemo(() => {
    return getDefaultSelectedChoices(allChoices, predictionMarketSnapshots);
  }, [allChoices, predictionMarketSnapshots]);

  // Calculate selected choice IDs
  const calculatedSelectedChoiceIds = useMemo(() => {
    if (defaultShowChartsIds && defaultShowChartsIds.length > 0) {
      // Get IDs from names specified in defaultShowCharts
      const validIds = defaultShowChartsIds.filter((choiceId) => allChoices.some((choice) => choice.id === choiceId));

      // Use valid IDs if found, otherwise use default Top 4
      return validIds.length > 0 ? validIds : defaultSelectedChoices;
    }
    return defaultSelectedChoices;
  }, [defaultShowChartsIds, allChoices, defaultSelectedChoices]);

  // State for selected choices
  const [selectedChoiceIds, setSelectedChoiceIds] = useState<string[]>(calculatedSelectedChoiceIds);

  // Filter selected choices
  const selectedChoices = useMemo(() => {
    return allChoices.filter((choice) => selectedChoiceIds.includes(choice.id));
  }, [allChoices, selectedChoiceIds]);

  // Create configuration for selected lines with fixed colors based on choice order
  const chartConfig = useMemo(() => {
    return createChartConfig(allChoices);
  }, [allChoices]) satisfies ChartConfig;

  const choiceProbabilityTimeline = bucketChoiceProbabilities(
    predictionMarketSnapshots,
    displayMode,
    endTime || Date.now(), // Use current time for both filtering and displaying current time data point
  );

  // Calculate Y-axis domain based on data
  const yAxisDomain = useMemo(() => {
    return calculateYAxisDomain(choiceProbabilityTimeline, selectedChoiceIds);
  }, [choiceProbabilityTimeline, selectedChoiceIds]);

  // Calculate custom ticks for different display modes
  const customTicksForAll = useMemo(() => {
    return getCustomTicksForAll(choiceProbabilityTimeline, displayMode);
  }, [choiceProbabilityTimeline, displayMode]);

  const customTicksFor1M = useMemo(() => {
    return getCustomTicksFor1M(choiceProbabilityTimeline, displayMode);
  }, [choiceProbabilityTimeline, displayMode]);

  const customTicksFor1W = useMemo(() => {
    return getCustomTicksFor1W(choiceProbabilityTimeline, displayMode);
  }, [choiceProbabilityTimeline, displayMode]);

  useEffect(() => {
    if (!isLineVisible) {
      setIsPointerEventsEnabled(false);
      return;
    }

    const timer = setTimeout(() => {
      setIsPointerEventsEnabled(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [isLineVisible]);

  return (
    <div>
      <div className="prediction-chart" style={{ pointerEvents: isPointerEventsEnabled ? "auto" : "none" }}>
        <ChartContainer
          config={chartConfig}
          className="h-[300px] w-full !justify-start"
          onTouchEnd={() => {
            setIsActiveDotShown(false);
            chartRef.current.handleItemMouseLeave();
          }}
          onTouchCancel={() => {
            setIsActiveDotShown(false);
            chartRef.current.handleItemMouseLeave();
          }}
          style={{
            touchAction: "pan-y",
          }}
        >
          <LineChart
            data={isLineVisible ? choiceProbabilityTimeline : []}
            key={isLineVisible ? "loaded" : "initial"}
            margin={{ left: 20, right: -20, top: 20, bottom: 0 }}
            onMouseDown={() => {
              if (!isLineVisible) return;
              setIsActiveDotShown(true);
            }}
            onMouseMove={() => {
              if (!isLineVisible) return;
              setIsActiveDotShown(true);
            }}
            onMouseUp={() => {
              if (!isLineVisible) return;
              setIsActiveDotShown(false);
              chartRef.current.handleItemMouseUp();
            }}
            onMouseLeave={() => {
              if (!isLineVisible) return;
              setIsActiveDotShown(false);
              chartRef.current.handleItemMouseLeave();
            }}
            ref={chartRef}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="time"
              stroke="#888888"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              textAnchor="middle"
              tickFormatter={(value) => getXAxisFormatter(value, displayMode)}
              interval={
                displayMode === "all" || displayMode === "1M" || displayMode === "1W"
                  ? "preserveStartEnd"
                  : getTickInterval(choiceProbabilityTimeline.length)
              }
              ticks={
                displayMode === "all"
                  ? customTicksForAll
                  : displayMode === "1M"
                    ? customTicksFor1M
                    : displayMode === "1W"
                      ? customTicksFor1W
                      : undefined
              }
            />
            <YAxis
              stroke="#888888"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `${value}%`}
              orientation="right"
              domain={yAxisDomain}
              ticks={yAxisDomain[1] <= 50 ? [0, 10, 20, 30, 40, 50] : [0, 25, 50, 75, 100]}
            />
            <ChartTooltip
              content={({ payload, active, label }) => {
                if (!active || !payload) return null;
                const customPayload = payload.map((item) => ({
                  ...item,
                  value: Number(item.value) >= 1 ? (item.value === "100" ? "99" : item.value) : "<1",
                }));
                return (
                  <ChartTooltipContent className="percentage" payload={customPayload} label={label} active={active} />
                );
              }}
              allowEscapeViewBox={{ x: false, y: false }}
              isAnimationActive={false}
              trigger="hover"
            />
            {selectedChoices.map((choice) => {
              const lineColor = chartConfig[choice.id]?.color ?? "#000000";
              return (
                <Line
                  key={choice.id}
                  type="monotone"
                  dataKey={choice.id}
                  stroke={lineColor}
                  strokeWidth={2}
                  animationEasing="ease"
                  animationDuration={500}
                  dot={(props) => {
                    // Latest Dot
                    if (props.index === choiceProbabilityTimeline.length - 1 && !isActiveDotShown) {
                      return <AnimatedDot {...props} color={lineColor} />;
                    }
                    return <></>;
                  }}
                  activeDot={(props: AnimatedDotProps) => {
                    if (!isActiveDotShown) {
                      return <></>;
                    }
                    return <AnimatedDot {...props} color={lineColor} />;
                  }}
                />
              );
            })}
          </LineChart>
        </ChartContainer>
        <div className="grid grid-cols-[1fr_auto] gap-2 items-stretch mt-4">
          <div className="w-full bg-[#F9F9F9] rounded-lg flex items-center justify-center py-2 px-4">
            <PredictionChartMenu displayMode={displayMode} setDisplayMode={setDisplayMode} />
          </div>
          <div className="w-full bg-[#F9F9F9] rounded-lg flex items-center">
            <div
              className="cursor-pointer px-4 py-2"
              onClick={() => {
                setIsFilterSelectOpen(true);
              }}
            >
              <FilterIcon />
            </div>
          </div>
        </div>
      </div>
      <FilterSelect
        chartConfig={chartConfig}
        allChoices={allChoices}
        selectedChoiceIds={selectedChoiceIds}
        setSelectedChoiceIds={(ids) => {
          setSelectedChoiceIds(ids);
          if (setChoiceIds) {
            setChoiceIds(ids);
          }
        }}
        isFilterSelectOpen={isFilterSelectOpen}
        onClose={() => setIsFilterSelectOpen(false)}
      />
    </div>
  );
};
